/**
 * 页面内容管理（/admin/pages/about——04 品牌叙事）：双语标题/正文块/SEO + 发布状态。
 * blocks 结构冻结于 contracts PageContent：[{heading, body}]。
 */
import { useCallback, useEffect, useState } from 'react';
import type { PageContent } from '@chronospeak/contracts';
import { adminHttp, ApiError } from '../lib/api';
import { Button } from '@chronospeak/ui';
import { ErrorNote, Loading, PageHeader, StatusBadge } from '../components/ui-bits';
import { TranslateButton, MachineDraftBadge } from '../components/TranslateButton';
import { useSession } from '../lib/session';

interface AdminPageDetail {
  key: string;
  status: string;
  translations: {
    'zh-CN': { title: string; blocks: { heading: string; body: string }[]; seoTitle: string; seoDescription: string; translationStatus: string } | null;
    en: { title: string; blocks: { heading: string; body: string }[]; seoTitle: string; seoDescription: string; translationStatus: string } | null;
  };
  updatedAt: string;
}

type BlockInput = { heading: string; body: string };

export function PagesAdminPage() {
  const [detail, setDetail] = useState<AdminPageDetail | null>(null);
  const { admin } = useSession();
  const [zhTitle, setZhTitle] = useState('');
  const [zhBlocks, setZhBlocks] = useState<BlockInput[]>([{ heading: '', body: '' }]);
  const [hasEn, setHasEn] = useState(false);
  const [enTitle, setEnTitle] = useState('');
  const [enBlocks, setEnBlocks] = useState<BlockInput[]>([{ heading: '', body: '' }]);
  const [seo, setSeo] = useState({ zhTitle: '', zhDesc: '', enTitle: '', enDesc: '' });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** 「机翻初稿」脏标记：机翻填充置 true，任一英文字段编辑后清 false */
  const [mtDraft, setMtDraft] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const d = await adminHttp.get<AdminPageDetail>('/admin/pages/about');
      setDetail(d);
      const zh = d.translations['zh-CN'];
      const en = d.translations.en;
      if (zh) {
        setZhTitle(zh.title);
        setZhBlocks(zh.blocks.length ? zh.blocks : [{ heading: '', body: '' }]);
        setSeo((s) => ({ ...s, zhTitle: zh.seoTitle, zhDesc: zh.seoDescription }));
      }
      if (en) {
        setHasEn(true);
        setEnTitle(en.title);
        setEnBlocks(en.blocks.length ? en.blocks : [{ heading: '', body: '' }]);
        setSeo((s) => ({ ...s, enTitle: en.seoTitle, enDesc: en.seoDescription }));
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '加载失败');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error && !detail) {
    return (
      <section>
        <PageHeader title='页面内容' />
        <ErrorNote message={error} />
      </section>
    );
  }
  if (!detail) {
    return (
      <section>
        <PageHeader title='页面内容' />
        <Loading />
      </section>
    );
  }

  const save = async () => {
    setError(null);
    setNotice(null);
    if (!zhTitle.trim()) return setError('标题（中文）必填。');
    const cleanZh = zhBlocks.filter((b) => b.heading.trim() || b.body.trim());
    const cleanEn = hasEn ? enBlocks.filter((b) => b.heading.trim() || b.body.trim()) : [];
    try {
      await adminHttp.patch<AdminPageDetail>('/admin/pages/about', {
        translations: {
          'zh-CN': {
            title: zhTitle,
            blocks: cleanZh,
            seoTitle: seo.zhTitle || zhTitle,
            seoDescription: seo.zhDesc,
          },
          ...(hasEn && enTitle.trim()
            ? {
                en: {
                  title: enTitle,
                  blocks: cleanEn,
                  seoTitle: seo.enTitle || enTitle,
                  seoDescription: seo.enDesc,
                },
              }
            : {}),
        },
      });
      setNotice('已保存。发布动作走下方按钮。');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '保存失败');
    }
  };

  const togglePublish = async () => {
    setError(null);
    const next = detail.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
    try {
      // PATCH pages 端点支持 status 字段（M1 已实现）
      const res = await adminHttp.patch<AdminPageDetail>('/admin/pages/about', {
        translations: {
          'zh-CN': {
            title: zhTitle,
            blocks: zhBlocks.filter((b) => b.heading.trim() || b.body.trim()),
            seoTitle: seo.zhTitle || zhTitle,
            seoDescription: seo.zhDesc,
          },
        },
        status: next,
      });
      setDetail(res);
      setNotice(next === 'PUBLISHED' ? '已发布。' : '已转草稿（前台不可见）。');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '操作失败');
    }
  };

  const blockEditor = (
    blocks: BlockInput[],
    setBlocks: (b: BlockInput[]) => void,
    onFieldEdit?: () => void,
  ) => (
    <>
      {blocks.map((b, i) => (
        <div key={i} className='mgr-block-row'>
          <label className='mgr-field'>
            小节标题
            <input
              value={b.heading}
              onChange={(e) => {
                setBlocks(blocks.map((x, j) => (j === i ? { ...x, heading: e.target.value } : x)));
                onFieldEdit?.();
              }}
            />
          </label>
          <label className='mgr-field'>
            正文（换行分段）
            <textarea
              rows={3}
              value={b.body}
              onChange={(e) => {
                setBlocks(blocks.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)));
                onFieldEdit?.();
              }}
            />
          </label>
          <Button variant='ghost' size='sm' onClick={() => setBlocks(blocks.filter((_, j) => j !== i))}>
            删除本节
          </Button>
        </div>
      ))}
      <Button variant='ghost' size='sm' onClick={() => setBlocks([...blocks, { heading: '', body: '' }])}>
        + 添加小节
      </Button>
    </>
  );

  return (
    <section>
      <PageHeader
        title='页面内容 · about'
        sub={<>品牌叙事页（时光广场入口目标）。状态 <StatusBadge status={detail.status} /></>}
        actions={
          <>
            <Button variant='ghost' size='sm' onClick={() => void togglePublish()}>
              {detail.status === 'PUBLISHED' ? '转回草稿' : '发布'}
            </Button>
            <Button variant='primary' size='sm' onClick={() => void save()}>
              保存
            </Button>
          </>
        }
      />
      {error && <ErrorNote message={error} />}
      {notice && (
        <p role='status' className='mgr-notice'>
          {notice}
        </p>
      )}

      <div className='mgr-form-grid'>
        <fieldset className='mgr-fieldset'>
          <legend>中文（必填）</legend>
          <label className='mgr-field'>
            页面标题 *<input value={zhTitle} onChange={(e) => setZhTitle(e.target.value)} />
          </label>
          {blockEditor(zhBlocks, setZhBlocks)}
          <label className='mgr-field'>
            SEO 标题<input value={seo.zhTitle} onChange={(e) => setSeo({ ...seo, zhTitle: e.target.value })} />
          </label>
          <label className='mgr-field'>
            SEO 描述<input value={seo.zhDesc} onChange={(e) => setSeo({ ...seo, zhDesc: e.target.value })} />
          </label>
        </fieldset>
        <fieldset className='mgr-fieldset'>
          <legend>
            <label style={{ display: 'inline', gap: 6 }}>
              <input type='checkbox' checked={hasEn} onChange={(e) => setHasEn(e.target.checked)} /> 英文
            </label>
            <MachineDraftBadge visible={mtDraft} />
          </legend>
          {hasEn && (
            <>
              <div style={{ marginBottom: 12 }}>
                <TranslateButton
                  mtEnabled={admin?.mtEnabled ?? false}
                  collectSources={() => [
                    zhTitle,
                    ...zhBlocks.flatMap((b) => [b.heading, b.body]).filter((s) => s.trim()),
                  ]}
                  onBeforeApply={() =>
                    [enTitle, ...enBlocks.flatMap((b) => [b.heading, b.body])].every((v) => !v.trim()) ||
                    window.confirm('英文区已有内容，确定用机器翻译结果覆盖？')
                  }
                  onApply={(translated) => {
                    // 第 1 段 = Title；其后按小节两两（heading/body）回填；SEO 字段不机翻（品牌词人工定）
                    const [title = '', ...rest] = translated;
                    const blocks: BlockInput[] = [];
                    for (let i = 0; i < rest.length; i += 2) {
                      blocks.push({ heading: rest[i] ?? '', body: rest[i + 1] ?? '' });
                    }
                    setEnTitle(title);
                    setEnBlocks(blocks.length ? blocks : [{ heading: '', body: '' }]);
                    setMtDraft(true);
                  }}
                />
              </div>
              <label className='mgr-field'>
                Title<input value={enTitle} onChange={(e) => { setEnTitle(e.target.value); setMtDraft(false); }} />
              </label>
              {blockEditor(enBlocks, setEnBlocks, () => setMtDraft(false))}
              <label className='mgr-field'>
                SEO Title<input value={seo.enTitle} onChange={(e) => setSeo({ ...seo, enTitle: e.target.value })} />
              </label>
              <label className='mgr-field'>
                SEO Description<input value={seo.enDesc} onChange={(e) => setSeo({ ...seo, enDesc: e.target.value })} />
              </label>
            </>
          )}
        </fieldset>
      </div>
    </section>
  );
}

/** 类型守卫：公开投影类型（contracts PageContent）在编辑保存时的等价形态 */
export type { PageContent };
